import React from "react";
import Select from "react-select";
import _ from "underscore";
import { useQueryState } from "shared/hooks";

/**
 * A control that wraps react-select, except when it changes it writes that state to the
 * query string. It has been somewhat styled to fit in to other bootstap pieces. The
 * variable that the selector will write to on the url can be customized with the
 * `queryVariable` prop, as well as the `defaultValue` being defined. The control can
 * also show a loading state while data is loading on the page.
 *
 * The `options` prop controls what is in the control. They are a list of elements, each
 * of which is of the form { value, label }. For example:
 * ```
 * const options = [
 *  { value: "chocolate", label: "Chocolate" },
 *  { value: "strawberry", label: "Strawberry" },
 *  { value: "vanilla", label: "Vanilla" },
 * ]
 * ```
 */
export const Selector = ({
    options,
    defaultChoice,
    queryVariable = "selection",
    isLoading = false,
    placeholder = "Select...",
    width = "100%",
}) => {
    // Manages the state of the choice on the query string
    const [value, setValue] = useQueryState(queryVariable, defaultChoice);

    // Make the drop down smaller to match Bootstrap input widgets
    const customStyles = {
        container: (provided) => ({
            ...provided,
            display: "inline-block",
            width,
            minHeight: "1px",
            textAlign: "left",
            border: "none",
            paddingBottom: 15,
        }),
        control: (provided) => ({
            ...provided,
            minHeight: "1px",
            height: "32px",
        }),
        input: (provided) => ({
            ...provided,
            minHeight: "1px",
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            minHeight: "1px",
            paddingTop: "0",
            paddingBottom: "0",
        }),
        indicatorSeparator: (provided) => ({
            ...provided,
            minHeight: "1px",
            height: "12px",
        }),
        clearIndicator: (provided) => ({
            ...provided,
            minHeight: "1px",
            paddingTop: "0",
            paddingBottom: "0",
        }),
        valueContainer: (provided) => ({
            ...provided,
            minHeight: "1px",
            height: "30px",
            paddingTop: "0",
            paddingBottom: "0",
        }),
        singleValue: (provided) => ({
            ...provided,
            minHeight: "1px",
            paddingBottom: "2px",
        }),
    };

    const current = _.find(options, (item) => value === item.value);

    return (
        <Select
            styles={customStyles}
            value={current}
            isDisabled={false}
            isClearable={true}
            isSearchable={true}
            blurInputOnSelect={true}
            isLoading={isLoading}
            placeholder={placeholder}
            options={options}
            onChange={(v) => setValue(v ? v.value : null)}
        />
    );
};
